import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip,
  FormGroup,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  AccessTime as TimeIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { scheduledUptimeApi } from "../services/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Common timezone options
const TIMEZONE_OPTIONS = [
  "Asia/Riyadh",
  "UTC",
  "GMT",
  "---", // Separator
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Australia/Sydney",
];

// Days of week for custom schedule
const DAYS_OF_WEEK = [
  { value: 0 },
  { value: 1 },
  { value: 2 },
  { value: 3 },
  { value: 4 },
  { value: 5 },
  { value: 6 },
];

const ScheduleUptimeDialog = ({
  open,
  onClose,
  deployment,
  onRefresh,
  editingSchedule,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    schedule_type: "DAILY",
    start_time: new Date(new Date().setHours(9, 0, 0, 0)), // 9:00 AM
    end_time: new Date(new Date().setHours(17, 0, 0, 0)), // 5:00 PM
    timezone: "Asia/Riyadh",
    days_of_week: [],
    is_active: true,
  });

  // Initialize form data when editing
  React.useEffect(() => {
    if (editingSchedule && open) {
      // Parse time strings to Date objects
      const parseTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(":");
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return date;
      };

      setFormData({
        name: editingSchedule.name || "",
        description: editingSchedule.description || "",
        schedule_type: (editingSchedule.schedule_type || "daily").toUpperCase(),
        start_time: parseTime(editingSchedule.start_time),
        end_time: parseTime(editingSchedule.end_time),
        timezone: editingSchedule.timezone || "Asia/Riyadh",
        days_of_week: editingSchedule.days_of_week || [],
        is_active:
          editingSchedule.is_active !== undefined
            ? editingSchedule.is_active
            : true,
      });
    } else if (!editingSchedule && open) {
      // Reset to default values for new schedule
      setFormData({
        name: "",
        description: "",
        schedule_type: "DAILY",
        start_time: new Date(new Date().setHours(9, 0, 0, 0)),
        end_time: new Date(new Date().setHours(17, 0, 0, 0)),
        timezone: "Asia/Riyadh",
        days_of_week: [],
        is_active: true,
      });
    }
  }, [editingSchedule, open]);

  const handleClose = () => {
    // Reset form
    setFormData({
      name: "",
      description: "",
      schedule_type: "DAILY",
      start_time: new Date(new Date().setHours(9, 0, 0, 0)),
      end_time: new Date(new Date().setHours(17, 0, 0, 0)),
      timezone: "Asia/Riyadh",
      days_of_week: [],
      is_active: true,
    });
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleScheduleTypeChange = (event) => {
    const scheduleType = event.target.value;
    let updatedFormData = {
      ...formData,
      schedule_type: scheduleType,
    };

    // Automatically set days_of_week for WEEKDAYS
    if (scheduleType === "WEEKDAYS") {
      updatedFormData.days_of_week = [0, 1, 2, 3, 4]; // Monday to Friday
    } else if (scheduleType === "WEEKLY") {
      updatedFormData.days_of_week = [0]; // Default to Monday
    } else if (scheduleType === "DAILY") {
      updatedFormData.days_of_week = [];
    }

    setFormData(updatedFormData);
  };

  const handleDayOfWeekChange = (day, checked) => {
    console.log("here", [...formData.days_of_week, day]);
    const updatedDays = checked
      ? [...formData.days_of_week, day].sort((a, b) => a - b)
      : formData.days_of_week.filter((d) => d !== day);
    console.log("updatedDays", updatedDays);
    handleInputChange("days_of_week", updatedDays);
  };

  const formatTimeForAPI = (date) => {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error(t("schedule.nameRequired"));
      return false;
    }

    if (
      formData.schedule_type === "CUSTOM" &&
      formData.days_of_week.length === 0
    ) {
      toast.error(t("schedule.customDayRequired"));
      return false;
    }

    if (
      formData.schedule_type === "WEEKLY" &&
      formData.days_of_week.length === 0
    ) {
      toast.error(t("schedule.weeklyDayRequired"));
      return false;
    }

    // Check if end time is after start time
    if (formData.end_time <= formData.start_time) {
      toast.error(t("schedule.endTimeAfterStart"));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const scheduleData = {
        deployment_id: deployment.model_id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        schedule_type: formData.schedule_type.toLowerCase(),
        start_time: formatTimeForAPI(formData.start_time),
        end_time: formatTimeForAPI(formData.end_time),
        timezone: formData.timezone,
        is_active: formData.is_active,
      };

      // Add days_of_week only for specific schedule types
      if (
        formData.schedule_type === "WEEKLY" ||
        formData.schedule_type === "CUSTOM"
      ) {
        scheduleData.days_of_week = formData.days_of_week;
      }

      if (editingSchedule) {
        // Update existing schedule
        await scheduledUptimeApi.updateSchedule(
          editingSchedule.id,
          scheduleData,
        );
        toast.success(t("schedule.updatedSuccess"));
      } else {
        // Create new schedule
        await scheduledUptimeApi.createSchedule(scheduleData);
        toast.success(t("schedule.createdSuccess"));
      }

      handleClose();
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error saving schedule:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("schedule.failedToSave");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getScheduleTypeDescription = () => {
    switch (formData.schedule_type) {
      case "DAILY":
        return t("schedule.dailyDescription");
      case "WEEKDAYS":
        return t("schedule.weekdaysDescription");
      case "WEEKLY":
        return t("schedule.weeklyDescription");
      case "CUSTOM":
        return t("schedule.customDescription");
      default:
        return "";
    }
  };

  const getWeekdayLabel = (dayValue, format = "long") => {
    const referenceDate = new Date(Date.UTC(2024, 0, 1 + dayValue));
    return new Intl.DateTimeFormat(locale, {
      weekday: format,
      timeZone: "UTC",
    }).format(referenceDate);
  };

  const renderDaySelector = () => {
    if (formData.schedule_type === "CUSTOM") {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("schedule.selectDays")}
          </Typography>
          <FormGroup row>
            {DAYS_OF_WEEK.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={formData.days_of_week.includes(day.value)}
                    onChange={(e) =>
                      handleDayOfWeekChange(day.value, e.target.checked)
                    }
                  />
                }
                label={getWeekdayLabel(day.value)}
              />
            ))}
          </FormGroup>
        </Box>
      );
    }

    if (formData.schedule_type === "WEEKLY") {
      return (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t("schedule.selectDay")}</InputLabel>
            <Select
              value={formData.days_of_week[0] || ""}
              onChange={(e) =>
                handleInputChange("days_of_week", [e.target.value])
              }
              label={t("schedule.selectDay")}
            >
              {DAYS_OF_WEEK.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {getWeekdayLabel(day.value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );
    }

    return null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "600px" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScheduleIcon />
            <Typography variant="h6">
              {editingSchedule
                ? t("schedule.editTitle", { name: deployment?.name })
                : t("schedule.createTitle", { name: deployment?.name })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {editingSchedule
              ? t("schedule.editSubtitle")
              : t("schedule.createSubtitle")}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                {t("schedule.basicInformation")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t("schedule.scheduleName")}
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                required
                placeholder={t("schedule.scheduleNamePlaceholder")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                value={formData.timezone}
                onChange={(event, newValue) => {
                  if (newValue && newValue !== "---") {
                    handleInputChange("timezone", newValue);
                  }
                }}
                options={TIMEZONE_OPTIONS}
                getOptionDisabled={(option) => option === "---"}
                renderOption={(props, option) => {
                  if (option === "---") {
                    return (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          borderTop: 1,
                          borderColor: "divider",
                          height: "1px",
                          padding: 0,
                          margin: "4px 0",
                          pointerEvents: "none",
                        }}
                      />
                    );
                  }
                  return (
                    <Box component="li" {...props}>
                      {option}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("schedule.timezone")}
                    placeholder={t("schedule.searchTimezone")}
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("schedule.descriptionOptional")}
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                fullWidth
                multiline
                rows={2}
                placeholder={t("schedule.descriptionPlaceholder")}
              />
            </Grid>

            {/* Schedule Type */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t("schedule.schedulePattern")}
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={formData.schedule_type}
                  onChange={handleScheduleTypeChange}
                >
                  <FormControlLabel
                    value="DAILY"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{t("schedule.daily")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("schedule.everyDay")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="WEEKDAYS"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{t("schedule.weekdaysOnly")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("schedule.mondayThroughFriday")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="WEEKLY"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{t("schedule.weekly")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("schedule.oncePerWeek")}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="CUSTOM"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{t("schedule.customDays")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("schedule.specificDaysOfWeek")}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {renderDaySelector()}

              <Alert severity="info" sx={{ mt: 2 }}>
                {getScheduleTypeDescription()}
              </Alert>
            </Grid>

            {/* Time Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t("schedule.timeConfiguration")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TimePicker
                label={t("schedule.startTime")}
                value={formData.start_time}
                onChange={(newValue) =>
                  handleInputChange("start_time", newValue)
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
                ampm={false}
              />
              <FormHelperText>
                {t("schedule.startTimeHelper")}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <TimePicker
                label={t("schedule.endTime")}
                value={formData.end_time}
                onChange={(newValue) => handleInputChange("end_time", newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                ampm={false}
              />
              <FormHelperText>
                {t("schedule.endTimeHelper")}
              </FormHelperText>
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t("schedule.summary")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                  <Chip
                    icon={<TimeIcon />}
                    label={
                      <span dir="ltr">
                        {`${formData.start_time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${formData.end_time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                      </span>
                    }
                    variant="outlined"
                  />
                  <Chip label={formData.timezone} variant="outlined" />
                  <Chip
                    label={
                      formData.schedule_type === "WEEKDAYS"
                        ? t("schedule.weekdaysOnly")
                        : formData.schedule_type === "DAILY"
                          ? t("schedule.daily")
                          : formData.schedule_type === "WEEKLY"
                            ? `${t("schedule.weekly")} (${getWeekdayLabel(formData.days_of_week[0], "short")})`
                            : `${t("schedule.customDays")} (${formData.days_of_week.map((d) => getWeekdayLabel(d, "short")).join(", ")})`
                    }
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {getScheduleTypeDescription()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} /> : <ScheduleIcon />
            }
          >
            {loading
              ? editingSchedule
                ? t("schedule.updatingAction")
                : t("schedule.creatingAction")
              : editingSchedule
                ? t("schedule.updateAction")
                : t("schedule.createAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ScheduleUptimeDialog;
